import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { products, categoryList } from "@/mocks/products";
import type { Product } from "@/mocks/products";

function ProductCard({ product }: { product: Product }) {
  const { t } = useTranslation();
  return (
    <div className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 flex flex-col h-full" data-product-shop="">
      <div className="relative aspect-square overflow-hidden bg-background-100">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-accent-100 text-accent-800">
            {product.category}
          </span>
        </div>
      </div>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <h3 className="text-sm font-medium text-foreground-950 leading-snug mb-2 line-clamp-2 flex-1">
          {product.name}
        </h3>
        <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-background-200/50">
          <div className="flex flex-col">
            <span className="text-base font-semibold text-primary-700">{product.price}</span>
            <span className="text-xs text-foreground-400">{product.priceCNY}</span>
          </div>
          <a
            href={product.link}
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-1" />
            {t("products.buy")}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const { lang = "en" } = useParams<{ lang?: string }>();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (activeCategory !== "all") {
      result = result.filter((p) => p.categoryKey === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const productCount = filteredProducts.length;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="container-limit mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-3xl">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
              {t("products.title")}
            </h1>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              {t("products.description")}
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-information-line" />
              <span>
                {t("products.disclaimer")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-[4.5rem] z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="container-limit mx-auto px-4 md:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            {/* Search */}
            <div className="relative flex-shrink-0 sm:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                placeholder={t("products.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            {/* Category count */}
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              {t("products.productCount", { count: productCount })}
            </span>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setActiveCategory("all")}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t("products.filterAll")}
            </button>
            {categoryList.map((cat) => {
              const count = products.filter((p) => p.categoryKey === cat.key).length;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeCategory === cat.key ? "text-background-50/80" : "text-foreground-400"}`}>
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="container-limit mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <i className="ri-search-line text-4xl text-foreground-300 mb-4" />
            <p className="text-lg font-medium text-foreground-700 mb-2">
              {t("products.noResults")}
            </p>
            <p className="text-sm text-foreground-500">
              {t("products.noResultsDesc")}
            </p>
            <button
              onClick={() => {
                setActiveCategory("all");
                setSearchQuery("");
              }}
              className="mt-4 btn-secondary text-sm"
            >
              {t("products.clearFilters")}
            </button>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-background-200/70 bg-background-100">
        <div className="container-limit mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            {t("products.moreProducts")}
          </h2>
          <p className="text-base text-foreground-700 mb-6 max-w-xl mx-auto">
            {t("products.moreProductsDesc")}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary text-base"
          >
            <i className="ri-external-link-line mr-2" />
            {t("bottomCta.secondaryCta")}
          </a>
        </div>
      </section>
    </div>
  );
}